import React, { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { CloseIcon } from '@/components/icons';
import { useCreateVideo, useUpdateVideo } from '@/lib/api/hooks/video';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import type { VideoItem } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import Modal from '@/pages/multimedia/videos/modals/Modal';
import { Button, Toggle } from '@/components/ui';
import FileUpload from '@/components/ui/FileUpload';
import Input from '@/components/ui/Input/Input';
import TextArea from '@/components/ui/TextArea/TextArea';

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  video?: VideoItem | null;
  folderId: string;
  onSave: () => void;
}

const VideoModalSchema = z.object({
  embeddedLink: z.string().trim().min(1, 'YouTube link is required'),
  title: z.string().trim().min(1, 'Title is required'),
  description: z.string().trim().optional().or(z.literal('')),
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
});

type VideoModalFormValues = z.infer<typeof VideoModalSchema>;

const VideoModal: React.FC<VideoModalProps> = ({ isOpen, onClose, video, folderId, onSave }) => {
  const isEditMode = !!video;

  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [useCustomThumbnail, setUseCustomThumbnail] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const createVideoMutation = useCreateVideo();
  const updateVideoMutation = useUpdateVideo();
  const { mutateAsync: uploadFile } = useFileUpload();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm<VideoModalFormValues>({
    resolver: zodResolver(VideoModalSchema),
    defaultValues: {
      embeddedLink: '',
      title: '',
      description: '',
      isKeyHighlight: false,
      isMultimedia: false,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (video) {
      reset({
        title: video.title,
        embeddedLink: video.embeddedLink,
        description: video.description ?? '',
        isKeyHighlight: Boolean(video.isKeyHighlight),
        isMultimedia: Boolean(video.isMultimedia),
      });
      setThumbnailUrl(video.thumbnailUrl ?? null);
      setUseCustomThumbnail(!!video.thumbnailUrl);
    } else {
      reset({
        title: '',
        embeddedLink: '',
        description: '',
        isKeyHighlight: false,
        isMultimedia: false,
      });
      setThumbnailUrl(null);
      setUseCustomThumbnail(false);
      setThumbnailFile(null);
    }
  }, [video, isOpen, reset]);

  const handleThumbnailSelect = (files: File[] | null) => {
    const file = files?.[0] ?? null;
    setThumbnailFile(file);
  };

  const removeThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailUrl(null);
  };

  const handleFormSubmit = async (values: VideoModalFormValues, status: 'DRAFT' | 'PUBLISH') => {
    if (status === 'DRAFT') {
      setIsSavingDraft(true);
    } else {
      setIsPublishing(true);
    }

    try {
      let finalThumbnailUrl: string | undefined = undefined;

      if (useCustomThumbnail && thumbnailFile) {
        setIsUploadingThumbnail(true);
        try {
          const uploadResult = await uploadFile({ file: thumbnailFile, category: 'IMAGE' });
          if (uploadResult.success && uploadResult.data?.fileUrl) {
            finalThumbnailUrl = uploadResult.data.fileUrl;
          } else {
            throw new Error('Thumbnail upload failed');
          }
        } finally {
          setIsUploadingThumbnail(false);
        }
      } else if (useCustomThumbnail && thumbnailUrl) {
        finalThumbnailUrl = thumbnailUrl;
      }

      if (isEditMode && video) {
        const updateData = {
          folderId,
          title: values.title.trim(),
          embeddedLink: values.embeddedLink.trim(),
          description: values.description?.trim() || undefined,
          isKeyHighlight: values.isKeyHighlight,
          isMultimedia: values.isMultimedia,
          thumbnailUrl: finalThumbnailUrl,
          status
        };

        const result = await updateVideoMutation.mutateAsync({
          videoId: video.videoUploadId,
          data: updateData,
        });

        if (result.success) {
          showToast.success('Video updated successfully');
          onSave();
          onClose();
        } else {
          throw new Error('Video update failed');
        }
      } else {
        const videoData = {
          folderId,
          title: values.title.trim(),
          embeddedLink: values.embeddedLink.trim(),
          description: values.description?.trim() || undefined,
          isKeyHighlight: values.isKeyHighlight,
          isMultimedia: values.isMultimedia,
          thumbnailUrl: finalThumbnailUrl,
          status,
        };

        const result = await createVideoMutation.mutateAsync(videoData);
        if (result.success) {
          showToast.success(`Video ${status === 'PUBLISH' ? 'published' : 'saved as draft'} successfully`);
          onSave();
          onClose();
        } else {
          throw new Error('Video creation failed');
        }
      }
    } catch (error) {
      console.error('Error saving video:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save video';
      showToast.error(errorMessage);
    } finally {
      if (status === 'DRAFT') {
        setIsSavingDraft(false);
      } else {
        setIsPublishing(false);
      }
    }
  };

  const handlePaste = () => {
    navigator.clipboard.readText().then((text) => {
      setValue('embeddedLink', text, { shouldDirty: true, shouldValidate: true });
      showToast.success('Link pasted from clipboard');
    }).catch(() => {
      showToast.error('Failed to read from clipboard');
    });
  };

  const isAnySubmitting = isSavingDraft || isPublishing || isUploadingThumbnail;

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-xl font-bold text-gray-800">{isEditMode ? 'Edit Video' : 'Add Video'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" disabled={isAnySubmitting}>
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-6">Paste embedded link from YouTube</p>

        <div className="space-y-4">
          <Controller
            name="embeddedLink"
            control={control}
            render={({ field }) => (
              <div>
                <div className="flex gap-2 items-end">
                  <Input
                    label="YouTube Link"
                    required
                    type="url"
                    placeholder="Enter YouTube link"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={errors.embeddedLink?.message}
                    disabled={isAnySubmitting}
                    $marginbottom="0"
                    inputWrapperClassName="w-full"
                  />
                  <Button
                    type="button"
                    onClick={handlePaste}
                    disabled={isAnySubmitting}
                    className="!px-6"
                  >
                    Paste
                  </Button>
                </div>
              </div>
            )}
          />

          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <Input
                label="Title"
                required
                placeholder="Enter video title"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.title?.message}
                disabled={isAnySubmitting}
                $marginbottom="0"
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextArea
                label="Description"
                placeholder="Enter description (optional)"
                value={field.value ?? ''}
                onChange={field.onChange}
                error={errors.description?.message}
                disabled={isAnySubmitting}
                containerClassName="mb-0"
                textAreaClassName="bg-[var(--inputBgColor)] min-h-[7.5rem] text-text-primary"
              />
            )}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Thumbnail</label>

            <Toggle
              checked={useCustomThumbnail}
              onChange={(nextChecked) => {
                setUseCustomThumbnail(nextChecked);
                if (!nextChecked) {
                  removeThumbnail();
                }
              }}
              label={useCustomThumbnail ? 'Upload Custom Thumbnail' : 'No custom thumbnail'}
              description="Use a custom cover image for this video."
              disabled={isAnySubmitting}
            />

            {useCustomThumbnail ? (
              <div className="space-y-3 mt-3">
                {!thumbnailFile && thumbnailUrl ? (
                  <div className="relative inline-block">
                    <img
                      src={thumbnailUrl}
                      alt="Custom thumbnail"
                      className="w-48 h-28 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={removeThumbnail}
                      disabled={isAnySubmitting}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-wine-100 disabled:opacity-50"
                    >
                      <CloseIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : null}

                <FileUpload
                  onChange={handleThumbnailSelect}
                  disabled={isAnySubmitting}
                  multiple={false}
                  allowedFileTypes={['image/*']}
                  $maxSizeMB={5}
                  placeholder="Click or drag an image here"
                  showPreview
                  containerClassName="min-h-[7rem]"
                />
              </div>
            ) : (
              <div className="w-48 h-28 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200">
                <span className="text-xs text-gray-400 text-center px-2">Video will display with its embed link</span>
              </div>
            )}

            <div className="md:col-span-2 rounded-xl border border-gray-200 bg-[#FCFDFC] p-4 space-y-4 mt-4">
              <Controller
                name="isKeyHighlight"
                control={control}
                render={({ field }) => (
                  <Toggle
                    checked={field.value}
                    onChange={field.onChange}
                    label="Mark as Key Highlight"
                    description="Marks the video as a key highlight."
                    disabled={isAnySubmitting}
                  />
                )}
              />

              <Controller
                name="isMultimedia"
                control={control}
                render={({ field }) => (
                  <Toggle
                    checked={field.value}
                    onChange={field.onChange}
                    label="Is Multimedia"
                    description="Marks the video as multimedia content."
                    disabled={isAnySubmitting}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              onClick={onClose}
              disabled={isAnySubmitting}
              variant="secondary"
              size="md"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit((values) => handleFormSubmit(values, 'DRAFT'))}
              disabled={isAnySubmitting}
              variant="secondary"
              size="md"
            >
              {isSavingDraft ? 'Saving...' : 'Save as draft'}
            </Button>
            <Button
              onClick={handleSubmit((values) => handleFormSubmit(values, 'PUBLISH'))}
              disabled={!isValid || isAnySubmitting}
              variant="primary"
              size="md"
            >
              {isPublishing ? 'Publishing...' : 'Publish'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default VideoModal;
